import notEmpty from '../../core/util/notEmpty';
import type { ProductVehicle } from '../types';
import type { FilterSelection } from './getVehicleFilterValues';
import { filterToVehicleFieldMap } from './getVehicleFilterValues';

const filterValuesFromList = (values: string[], list: string[]) => {
    if (list.length === 0) {
        return false;
    }

    return values.some(valueItem => list.includes(valueItem));
};

const filterByModelYear = (values: string[], vehicle: ProductVehicle) => {
    const { manufacturingStartYear, manufacturingEndYear } = vehicle;

    if (manufacturingEndYear === 0) {
        return values.some(
            filterValue => Number(filterValue) >= manufacturingStartYear
        );
    }

    return values.some(
        filterValue =>
            Number(filterValue) >= manufacturingStartYear &&
            Number(filterValue) <= manufacturingEndYear
    );
};

const filterByCcmRange = (values: string[], ccm: number) => {
    return values.some(filterValue => {
        if (filterValue.indexOf('<') !== -1) {
            const exactValue = Number(filterValue.replace('<', ''));
            return ccm < exactValue;
        }

        if (filterValue.indexOf('>') !== -1) {
            const exactValue = Number(filterValue.replace('>', ''));
            return ccm > exactValue;
        }

        const [lower, upper] = filterValue.split('-');
        return ccm >= Number(lower) && ccm <= Number(upper);
    });
};

const filterVehicle = (
    vehicle: ProductVehicle,
    filtersArray: [keyof FilterSelection, string[] | string][],
    useRangeForCcm: boolean
) => {
    return filtersArray.every(([name, selectedValue]) => {
        const values = Array.isArray(selectedValue)
            ? selectedValue
            : [selectedValue].filter(item => item != '' && notEmpty(item));
        if (values.length > 0) {
            const value = vehicle[filterToVehicleFieldMap[name]];

            if (name === 'typeApprovalList') {
                return filterValuesFromList(values, value as string[]);
            }

            if (name === 'type') {
                return filterValuesFromList(values, value as string[]);
            }

            if (name === 'modelYear') {
                return filterByModelYear(values, vehicle);
            }

            if (useRangeForCcm && name === 'ccm') {
                return filterByCcmRange(values, vehicle.ccm);
            }

            return values.includes(`${value}`);
        }
        return true;
    });
};

const filterVehicles = (
    vehicles: ProductVehicle[],
    filters: FilterSelection,
    useRangeForCcm = false
): [boolean, ProductVehicle[]] => {
    const filtersArray = Object.entries(filters) as [
        keyof FilterSelection,
        string[] | string,
    ][];

    const filteredVehicles = vehicles.filter(vehicle =>
        filterVehicle(vehicle, filtersArray, useRangeForCcm)
    );

    const areFiltersActive = filtersArray.some(
        ([, values]) => values && values.length > 0
    );

    return [areFiltersActive, filteredVehicles];
};

export default filterVehicles;
